import { Client, ReconnectionTimeMode } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import {
  dispatchNotificationDegraded,
  dispatchNotificationRefresh,
} from "@/utils/messageCenter";
import {
  getCurrentAccessToken,
  getLatestAccessToken,
  refreshAccessTokenSilently,
} from "@/utils/authTokenManager";

const SUBSCRIPTION_DESTINATION = "/user/queue/notifications";
const HEARTBEAT_INTERVAL = 10000;
const BASE_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 15000;
const DEGRADE_AFTER_FAILURES = 2;
const DEGRADED_RETRY_INTERVAL = 60 * 1000;

let stompClient = null;
let connectedToken = "";
let initialized = false;
let connectionFailures = 0;
let degradedMode = false;
let degradedRetryTimer = null;
let degradedPollTimer = null;
let lastFailureAt = 0;
let authRedirecting = false;
let authRecoveryPromise = null;
let socketLifecyclePromise = Promise.resolve();
let clientDeactivationPromise = Promise.resolve();

function enqueueSocketLifecycle(operation) {
  const result = socketLifecyclePromise.then(operation, operation);
  socketLifecyclePromise = result.catch(() => {});
  return result;
}

function deactivateClient(client, options) {
  if (!client) return Promise.resolve();
  const operation = clientDeactivationPromise.then(
    () => client.deactivate(options),
    () => client.deactivate(options),
  );
  clientDeactivationPromise = operation.catch(() => {});
  return operation;
}

function getNotificationSocketUrl() {
  const configuredBase = process.env.VUE_APP_GC_URL || "";
  const configuredWsPath = process.env.VUE_APP_NOTIFICATION_WS_PATH || "";
  const configuredBaseUrl = /^https?:\/\//.test(configuredBase)
    ? new URL(configuredBase)
    : null;

  if (/^https?:\/\//.test(configuredWsPath)) {
    return configuredWsPath.replace(/\/$/, "");
  }

  if (configuredWsPath.startsWith("/")) {
    const origin = configuredBaseUrl
      ? configuredBaseUrl.origin
      : window.location.origin;
    return `${origin}${configuredWsPath.replace(/\/$/, "")}`;
  }

  const basePath = configuredBaseUrl
    ? configuredBaseUrl.pathname.replace(/\/$/, "")
    : "/chain-api";
  const origin = configuredBaseUrl
    ? configuredBaseUrl.origin
    : window.location.origin;
  return `${origin}${basePath}/ws/notify`;
}

function getInitialReconnectDelay() {
  return BASE_RECONNECT_DELAY + Math.floor(Math.random() * 1000);
}

function clearDegradedRetryTimer() {
  if (!degradedRetryTimer) return;
  window.clearTimeout(degradedRetryTimer);
  degradedRetryTimer = null;
}

function clearDegradedPollTimer() {
  if (!degradedPollTimer) return;
  window.clearTimeout(degradedPollTimer);
  degradedPollTimer = null;
}

function isPageVisible() {
  return typeof document === "undefined" || document.visibilityState === "visible";
}

function scheduleDegradedPolling() {
  clearDegradedPollTimer();
  if (!degradedMode) return;
  degradedPollTimer = window.setTimeout(() => {
    degradedPollTimer = null;
    if (degradedMode && isPageVisible()) {
      dispatchNotificationRefresh("degraded-poll");
    }
    scheduleDegradedPolling();
  }, 30 * 1000);
}

function scheduleDegradedRetry() {
  clearDegradedRetryTimer();
  degradedRetryTimer = window.setTimeout(() => {
    degradedRetryTimer = null;
    if (getCurrentAccessToken()) connectNotificationSocket();
  }, DEGRADED_RETRY_INTERVAL);
}

function enterDegradedMode(reason = "connection-failed") {
  if (!degradedMode) {
    degradedMode = true;
    dispatchNotificationDegraded(true, reason);
    if (isPageVisible()) dispatchNotificationRefresh("degraded");
  }
  scheduleDegradedRetry();
  scheduleDegradedPolling();
}

export function isNotificationDegraded() {
  return degradedMode;
}

function handlePushMessage(message) {
  try {
    const payload = JSON.parse(message.body);
    if (payload && payload.type === "NEW_MESSAGE") {
      dispatchNotificationRefresh("new-message");
    }
  } catch (error) {
    console.warn("[消息中心] 无法解析推送消息", error);
  }
}

function getAuthorizationHeader(token) {
  return token.startsWith("Bearer ") ? token : `Bearer ${token}`;
}

async function disconnectNotificationSocketInternal(
  options = {},
  preserveDegradedState = false,
) {
  connectedToken = "";
  if (!preserveDegradedState) {
    connectionFailures = 0;
    clearDegradedRetryTimer();
    clearDegradedPollTimer();
    if (degradedMode) {
      degradedMode = false;
      dispatchNotificationDegraded(false, "disconnected");
    }
  }
  if (!stompClient) return;

  const client = stompClient;
  stompClient = null;
  try {
    await deactivateClient(client, options);
  } catch (error) {
    console.warn("[消息中心] 断开推送连接失败", error);
  }
}

export function disconnectNotificationSocket(options) {
  return enqueueSocketLifecycle(() =>
    disconnectNotificationSocketInternal(options),
  );
}

async function connectNotificationSocketInternal() {
  await clientDeactivationPromise;
  const storedToken = getCurrentAccessToken();
  if (!storedToken) {
    await disconnectNotificationSocketInternal();
    return;
  }
  const token =
    (await getLatestAccessToken({ refreshIfExpiring: true })) || storedToken;
  if (!token) {
    await disconnectNotificationSocketInternal();
    return;
  }

  if (stompClient && connectedToken === token && stompClient.active) return;
  await disconnectNotificationSocketInternal({}, degradedMode);
  authRedirecting = false;
  connectedToken = token;
  let failureRecordedForCurrentSocket = false;

  const client = new Client({
    webSocketFactory: () => {
      return new SockJS(getNotificationSocketUrl(), null, {
        // Proxies and audit gateways can buffer xhr_streaming forever. Treat
        // websocket failure as a signal to use REST instead of creating a
        // zombie SockJS HTTP session.
        transports: ["websocket"],
      });
    },
    connectHeaders: {
      Authorization: getAuthorizationHeader(token),
    },
    beforeConnect: async (stomp) => {
      const latestToken =
        (await getLatestAccessToken({ refreshIfExpiring: true })) || token;
      if (client === stompClient) connectedToken = latestToken;
      stomp.connectHeaders = {
        Authorization: getAuthorizationHeader(latestToken),
      };
    },
    heartbeatIncoming: HEARTBEAT_INTERVAL,
    heartbeatOutgoing: HEARTBEAT_INTERVAL,
    reconnectDelay: getInitialReconnectDelay(),
    reconnectTimeMode: ReconnectionTimeMode.EXPONENTIAL,
    maxReconnectDelay: MAX_RECONNECT_DELAY,
    debug: () => {},
    onConnect(frame) {
      if (client !== stompClient) return;
      const latestToken = getCurrentAccessToken();
      if (latestToken && latestToken !== connectedToken) {
        connectNotificationSocket();
        return;
      }

      connectionFailures = 0;
      failureRecordedForCurrentSocket = false;
      clearDegradedRetryTimer();
      clearDegradedPollTimer();
      if (degradedMode) {
        degradedMode = false;
        dispatchNotificationDegraded(false, "connected");
      }
      client.subscribe(SUBSCRIPTION_DESTINATION, handlePushMessage);

      if (frame.headers["heart-beat"] !== "10000,10000") {
        console.warn(
          "[消息中心] STOMP 心跳协商结果异常:",
          frame.headers["heart-beat"],
        );
      }
      dispatchNotificationRefresh("connected");
    },
    onWebSocketClose(event) {
      if (isNotificationAuthError(event)) {
        failureRecordedForCurrentSocket = false;
        recoverNotificationAuthentication(client, event);
        return;
      }
      if (
        event.code !== 1000 &&
        connectedToken &&
        !failureRecordedForCurrentSocket
      ) {
        recordConnectionFailure(client);
      }
      failureRecordedForCurrentSocket = false;
    },
    onStompError(frame) {
      if (isNotificationAuthError(frame)) {
        recoverNotificationAuthentication(client, frame);
        return;
      }
      failureRecordedForCurrentSocket = true;
      recordConnectionFailure(client);
      console.warn("[消息中心] STOMP 连接错误", frame.headers.message || "");
    },
    onWebSocketError(error) {
      if (isNotificationAuthError(error)) {
        recoverNotificationAuthentication(client, error);
        return;
      }
      failureRecordedForCurrentSocket = true;
      recordConnectionFailure(client);
      console.warn("[消息中心] WebSocket 连接失败", error);
    },
  });

  stompClient = client;
  try {
    client.activate();
  } catch (error) {
    stompClient = null;
    connectedToken = "";
    console.warn("[消息中心] 启动推送连接失败", error);
  }
}

export function connectNotificationSocket() {
  return enqueueSocketLifecycle(connectNotificationSocketInternal);
}

function isNotificationAuthError(frame) {
  const text = [
    frame && frame.headers && frame.headers.message,
    frame && frame.headers && frame.headers["message"],
    frame && frame.body,
    frame && frame.message,
    frame && frame.reason,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return /\b401\b|unauthorized|authentication failed|invalid token|认证失败|未认证|登录态|令牌.*(过期|无效)|token.*(过期|无效|expired|invalid)/i.test(
    text,
  );
}

async function recoverNotificationAuthentication(client, details) {
  if (!client || client !== stompClient || authRecoveryPromise) return;

  authRecoveryPromise = (async () => {
    const rejectedToken = connectedToken || getCurrentAccessToken();
    client.reconnectDelay = 0;
    stompClient = null;
    connectedToken = "";

    try {
      await deactivateClient(client, { force: true });
    } catch (error) {
      console.warn("[消息中心] 停止无效推送连接失败", error);
    }

    const currentToken = getCurrentAccessToken();
    const renewedToken =
      currentToken && currentToken !== rejectedToken
        ? currentToken
        : await refreshAccessTokenSilently();

    if (renewedToken && renewedToken !== rejectedToken) {
      authRedirecting = false;
      await connectNotificationSocket();
      return;
    }

    handleNotificationAuthFailure(null, details, true);
  })().finally(() => {
    authRecoveryPromise = null;
  });

  return authRecoveryPromise;
}

function handleNotificationAuthFailure(client, details, alreadyDeactivated = false) {
  if (authRedirecting) return;
  if (!alreadyDeactivated && (!client || client !== stompClient)) return;
  authRedirecting = true;
  if (client) client.reconnectDelay = 0;
  stompClient = null;
  connectedToken = "";
  if (client && !alreadyDeactivated) {
    deactivateClient(client, { force: true }).catch(() => {});
  }
  import("@/utils/storageHelper.js")
    .then(({ clearStorageAndRedirectToLogin }) => {
      clearStorageAndRedirectToLogin();
    })
    .catch(() => {
      window.localStorage.removeItem("t_k");
      const baseUrl = `${window.location.origin}${process.env.BASE_URL || "/"}`;
      const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
      window.location.replace(`${normalizedBaseUrl}#/login`);
    });
  console.warn("[消息中心] 推送认证失败，已断开连接并跳转登录", details);
}

function recordConnectionFailure(client) {
  if (!connectedToken || client !== stompClient) return;

  // SockJS commonly emits error and close for the same failed attempt.
  const now = Date.now();
  if (now - lastFailureAt < 100) return;
  lastFailureAt = now;

  connectionFailures += 1;
  if (connectionFailures < DEGRADE_AFTER_FAILURES) return;

  // Stop STOMP's fast reconnect loop. The degraded REST mode owns the next
  // retry and keeps it at one attempt per minute.
  client.reconnectDelay = 0;
  const failedClient = client;
  stompClient = null;
  connectedToken = "";
  deactivateClient(failedClient, { force: true }).catch(() => {});
  enterDegradedMode("connection-failed");
  console.warn("[消息中心] 推送连接失败，已降级为 REST 轮询，60 秒后重试 WS");
}

export function syncNotificationSocket(event) {
  const isTokenSyncEvent =
    event &&
    event.type === "tokenSynced" &&
    event.detail;
  if (isTokenSyncEvent && event.detail.key !== "t_k") {
    return;
  }
  if (authRecoveryPromise) return;

  const token = getCurrentAccessToken();
  // beforeConnect always reads the latest token. Restarting while it is still
  // running would only abort that connection and create a duplicate attempt.
  if (
    isTokenSyncEvent &&
    stompClient &&
    stompClient.active &&
    !stompClient.connected
  ) {
    return;
  }

  if (token) {
    connectNotificationSocket();
  } else {
    disconnectNotificationSocket();
  }
}

function handleStorageChange(event) {
  if (!event || event.key === "t_k" || event.key === null) {
    syncNotificationSocket();
  }
}

function handleVisibilityChange() {
  if (document.visibilityState === "visible") {
    syncNotificationSocket();
    if (degradedMode) {
      dispatchNotificationRefresh("visibility");
      scheduleDegradedPolling();
    } else if (!stompClient || !stompClient.connected) {
      dispatchNotificationRefresh("visibility");
    }
  }
}

export function initNotificationSocket() {
  if (initialized) return;
  initialized = true;
  window.addEventListener("storage", handleStorageChange);
  window.addEventListener("tokenSynced", syncNotificationSocket);
  document.addEventListener("visibilitychange", handleVisibilityChange);
  syncNotificationSocket();
}

export function destroyNotificationSocket() {
  if (initialized) {
    window.removeEventListener("storage", handleStorageChange);
    window.removeEventListener("tokenSynced", syncNotificationSocket);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    initialized = false;
  }
  return disconnectNotificationSocket({ force: true });
}

if (module.hot) {
  module.hot.dispose(() => {
    destroyNotificationSocket();
  });
}
